import { useEffect } from 'react'
import { useOrg } from '../../components/OrgLayout'
import { useOrgAnalytics } from '../../hooks/useOrgAnalytics'
import { useOrgPermissions } from '../../hooks/useOrgPermissions'
import {
  VStack,
  HStack,
  Card,
  Text,
  Grid,
  StatCard,
  Skeleton,
  Banner,
  PageHeader,
  Animate,
  EmptyState,
  Select,
  Table,
  Avatar
} from 'wss3-forge'
import {
  DataUsage20Regular,
  People20Regular,
  Checkmark20Regular,
  Dismiss20Regular,
  ArrowTrending20Regular
} from '@fluentui/react-icons'

// Simple bar chart component
function SimpleBarChart({ data, maxValue }: { data: { label: string; value: number; color?: string }[]; maxValue: number }) {
  return (
    <VStack gap="sm">
      {data.map((item, i) => (
        <HStack key={i} gap="md" style={{ alignItems: 'center' }}>
          <Text size="sm" style={{ width: 80, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
            {item.label}
          </Text>
          <div style={{ flex: 1, height: 24, backgroundColor: 'var(--bg-tertiary)', borderRadius: 4, overflow: 'hidden' }}>
            <div
              style={{
                width: `${maxValue > 0 ? (item.value / maxValue) * 100 : 0}%`,
                height: '100%',
                backgroundColor: item.color || 'var(--brand-primary)',
                borderRadius: 4,
                transition: 'width 0.3s ease'
              }}
            />
          </div>
          <Text size="sm" weight="medium" style={{ width: 50, textAlign: 'right' }}>
            {item.value}
          </Text>
        </HStack>
      ))}
    </VStack>
  )
}

// Simple line chart (sparkline style)
function SimpleLineChart({ data }: { data: { date: string; success: number; failed: number }[] }) {
  if (data.length === 0) return <Text size="sm" color="muted">No data</Text>

  const maxValue = Math.max(...data.map(d => d.success + d.failed), 1)

  return (
    <VStack gap="sm">
      <div style={{ display: 'flex', alignItems: 'flex-end', height: 120, gap: 2 }}>
        {data.map((d, i) => {
          const successHeight = (d.success / maxValue) * 100
          const failedHeight = (d.failed / maxValue) * 100

          return (
            <div
              key={i}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                height: '100%'
              }}
            >
              {d.failed > 0 && (
                <div
                  style={{
                    height: `${failedHeight}%`,
                    backgroundColor: 'var(--status-error)',
                    borderRadius: '2px 2px 0 0',
                    minHeight: d.failed > 0 ? 2 : 0
                  }}
                  title={`${d.date}: ${d.failed} failed`}
                />
              )}
              <div
                style={{
                  height: `${successHeight}%`,
                  backgroundColor: 'var(--status-success)',
                  borderRadius: d.failed > 0 ? '0' : '2px 2px 0 0',
                  minHeight: d.success > 0 ? 2 : 0
                }}
                title={`${d.date}: ${d.success} success`}
              />
            </div>
          )
        })}
      </div>
      <HStack style={{ justifyContent: 'space-between' }}>
        <Text size="xs" color="muted">{data[0]?.date}</Text>
        <HStack gap="md">
          <HStack gap="xs" style={{ alignItems: 'center' }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: 'var(--status-success)' }} />
            <Text size="xs" color="muted">Success</Text>
          </HStack>
          <HStack gap="xs" style={{ alignItems: 'center' }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: 'var(--status-error)' }} />
            <Text size="xs" color="muted">Failed</Text>
          </HStack>
        </HStack>
        <Text size="xs" color="muted">{data[data.length - 1]?.date}</Text>
      </HStack>
    </VStack>
  )
}

const PERIOD_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' }
]

export function OrgAnalytics() {
  const { organization } = useOrg()
  const { analytics, loading, error, period, setPeriod, loadAnalytics } = useOrgAnalytics()
  const { loadPermissions, canViewAnalytics } = useOrgPermissions()

  useEffect(() => {
    loadAnalytics(organization.id)
    loadPermissions(organization.id)
  }, [organization.id, loadAnalytics, loadPermissions])

  useEffect(() => {
    if (organization.id) {
      loadAnalytics(organization.id)
    }
  }, [period, organization.id, loadAnalytics])

  if (!canViewAnalytics && !loading) {
    return (
      <Banner variant="error" title="Access Denied">
        You don't have permission to view analytics.
      </Banner>
    )
  }

  if (loading && !analytics) {
    return (
      <VStack gap="lg">
        <Skeleton width={200} height={32} />
        <Grid columns={{ xs: 1, sm: 2, lg: 4 }} gap="md">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} width="100%" height={100} />
          ))}
        </Grid>
        <Skeleton width="100%" height={200} />
      </VStack>
    )
  }

  const hasData = analytics && (
    analytics.total_logins_period > 0 ||
    analytics.active_users_7d > 0 ||
    analytics.active_users_30d > 0
  )

  return (
    <VStack gap="lg">
      <Animate type="fadeIn">
        <PageHeader
          title="Analytics"
          subtitle="Usage statistics for your organization."
          actions={
            <div style={{ width: 160 }}>
              <Select
                value={String(period)}
                onChange={(val) => setPeriod(Number(val) as 7 | 30 | 90)}
                options={PERIOD_OPTIONS}
              />
            </div>
          }
        />
      </Animate>

      {error && (
        <Banner variant="error" title="Error">
          {error}
        </Banner>
      )}

      {!hasData ? (
        <Animate type="fadeIn" delay={100}>
          <EmptyState
            icon={<DataUsage20Regular />}
            title="No analytics data"
            description="Once your users start logging in via SSO, you'll see usage statistics here."
          />
        </Animate>
      ) : (
        <>
          <Animate type="slideInUp" delay={50}>
            <Grid columns={{ xs: 1, sm: 2, lg: 4 }} gap="md">
              <StatCard
                label="Active Users (7d)"
                value={analytics?.active_users_7d || 0}
                icon={<People20Regular />}
                color="var(--brand-primary)"
              />
              <StatCard
                label="Active Users (30d)"
                value={analytics?.active_users_30d || 0}
                icon={<People20Regular />}
                color="#10B981"
              />
              <StatCard
                label={`Total Logins (${period}d)`}
                value={analytics?.total_logins_period || 0}
                icon={<ArrowTrending20Regular />}
                color="#F59E0B"
              />
              <StatCard
                label="Success Rate"
                value={`${analytics?.success_rate || 0}%`}
                icon={analytics?.success_rate && analytics.success_rate >= 95 ? <Checkmark20Regular /> : <Dismiss20Regular />}
                color={analytics?.success_rate && analytics.success_rate >= 95 ? '#10B981' : '#EF4444'}
              />
            </Grid>
          </Animate>

          <Animate type="slideInUp" delay={100}>
            <Grid columns={{ xs: 1, lg: 2 }} gap="lg">
              <Card padding="lg" title="Logins Over Time">
                <SimpleLineChart data={analytics?.logins_by_day || []} />
              </Card>

              <Card padding="lg" title="Logins by Application">
                {analytics?.logins_by_app && analytics.logins_by_app.length > 0 ? (
                  <SimpleBarChart
                    data={analytics.logins_by_app.map(app => ({
                      label: app.app_name,
                      value: app.count,
                      color: 'var(--brand-primary)'
                    }))}
                    maxValue={Math.max(...analytics.logins_by_app.map(a => a.count))}
                  />
                ) : (
                  <Text size="sm" color="muted">No data available</Text>
                )}
              </Card>
            </Grid>
          </Animate>

          <Animate type="slideInUp" delay={150}>
            <Table
              title="Top Users"
              columns={[
                {
                  key: 'rank',
                  header: '#',
                  width: '50px',
                  render: (_, _row, index) => (
                    <Text size="sm" color="muted">{(index || 0) + 1}</Text>
                  )
                },
                {
                  key: 'email',
                  header: 'User',
                  render: (_, row) => (
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <Avatar name={row.email} size="sm" />
                      <Text size="sm">{row.email}</Text>
                    </HStack>
                  )
                },
                {
                  key: 'login_count',
                  header: 'Logins',
                  width: '100px',
                  render: (_, row) => (
                    <Text size="sm" weight="medium">{row.login_count}</Text>
                  )
                },
                {
                  key: 'last_login',
                  header: 'Last Login',
                  width: '180px',
                  render: (_, row) => (
                    <Text size="sm" color="muted">
                      {new Date(row.last_login).toLocaleString()}
                    </Text>
                  )
                }
              ]}
              data={analytics?.top_users || []}
            />
          </Animate>
        </>
      )}
    </VStack>
  )
}
